import React from "react";
import { useLocation, useParams } from "react-router-dom";

import { ASYNC_TASK_NAMES } from "@performyard/models";
import { Breadcrumbs as PyBreadcrumbs, Text } from "@performyard/py-components";

import Breadcrumbs from "../../../components/Breadcrumbs";
import { Divider } from "../../../components/Divider";
import DetailsOptionsDropdown from "./DetailsOptionsDropdown";
import ReviewCycleReviewsDetailsTable from "./Table";

import { useIsNewNavigation } from "../../../api/hooks/defs/navigation";
import useScrollToTopOfPageOnMount from "../../../hooks/useScrollToTopOfPageOnMount";
import {
	ReviewCycleBuilderPageProvider,
	useReviewCycleBuilderPageContext,
} from "./hooks/ReviewCycleBuilderPageContext";

import { currentUserQuery, reviewCycleDetailsQuery, reviewCycleReviewsDetailsStreamQuery } from "../../../api/queries";
import { dataLoader } from "../../../api/queries/dataLoader";
import { SimplifiedQuery } from "../../../api/queries/dataLoader";
import { queryClient } from "../../../api/queryClient";
import { getActiveCompanyTasksByName } from "../../../api/services/defs/internal-api/async-tasks";

import BasePageTemplate from "../../../pageTemplates/BasePageTemplate";
import FormCompletionCard from "./Cards/FormCompletionCard";
import ReviewAutomationCard from "./Cards/ReviewAutomationCard";
import ReviewCompletionCard from "./Cards/ReviewCompletionCard";
import ReviewCycleInformationCard from "./Cards/ReviewCycleInformationCard";

export const reviewCycleDetailsLoader = async ({ params }: { params: { cycleId?: string } }) => {
	const cycleId = params.cycleId!;

	const activeTasks = await queryClient
		.ensureQueryData({
			queryKey: ["active-create-review-cycle-tasks", cycleId],
			queryFn: () => getActiveCompanyTasksByName(ASYNC_TASK_NAMES.CREATE_REVIEW_CYCLE),
		})
		.catch(() => []);
	const isBuilding = activeTasks.some((t) => {
		const metadata = t.metadata as { cycleId?: string } | undefined;
		return metadata?.cycleId === cycleId;
	});
	if (isBuilding) return null;

	const mainQuery: (() => SimplifiedQuery)[] = [
		() => reviewCycleDetailsQuery(cycleId),
		() => reviewCycleReviewsDetailsStreamQuery(cycleId),
	];

	return dataLoader([[currentUserQuery], mainQuery]);
};

const ReviewCycleDetailsContent = () => {
	useScrollToTopOfPageOnMount();
	const { cycleId: reviewCycleId = "" } = useParams();
	const location = useLocation();
	const isNewNav = useIsNewNavigation();
	const { renderPage, reviewCycleDetails } = useReviewCycleBuilderPageContext();

	const reviewCycleName = reviewCycleDetails?.name;

	const hasAutomation = !!reviewCycleDetails?.autoflow;
	const cardWidth = hasAutomation ? "normal" : "wide";

	if (renderPage) {
		return renderPage;
	}

	return (
		<BasePageTemplate title="Review Cycle Details">
			<div className="mt-2">
				{isNewNav ? (
					<PyBreadcrumbs
						items={[
							{
								pageId: "review-cycles",
								name: "Review Cycles",
								href: "/company/reviews/cycles/?name=start_date&direction=desc",
							},
							{
								pageId: "review-cycle-details",
								name: reviewCycleName,
								href: location.pathname,
							},
						]}
					/>
				) : (
					<Breadcrumbs
						pages={[
							{
								pageID: "review-cycles",
								label: "Review Cycles",
								url: "/company/reviews/cycles",
							},
							{
								pageID: "review-cycle-details",
								label: reviewCycleName,
								url: `/review-cycle/${reviewCycleId}`,
							},
						]}
					/>
				)}
			</div>
			<div className="mt-4 flex justify-between">
				<Text size="heading-5" weight="semi-bold">
					{reviewCycleName}
				</Text>
				<DetailsOptionsDropdown reviewCycleId={reviewCycleId} />
			</div>
			<div className="mt-6 flex flex-wrap items-center justify-center gap-2 2xl:justify-between">
				<ReviewCycleInformationCard width={cardWidth} />
				<ReviewAutomationCard />
				<ReviewCompletionCard width={cardWidth} />
				<FormCompletionCard width={cardWidth} />
			</div>

			<Divider className="my-6" />
			<ReviewCycleReviewsDetailsTable reviewCycleId={reviewCycleId} />
		</BasePageTemplate>
	);
};

const ReviewCycleDetails = () => {
	const { cycleId: reviewCycleId = "" } = useParams();

	return (
		<ReviewCycleBuilderPageProvider reviewCycleId={reviewCycleId}>
			<ReviewCycleDetailsContent />
		</ReviewCycleBuilderPageProvider>
	);
};

export default ReviewCycleDetails;
